import { createSlice } from '@reduxjs/toolkit';
import semver from 'semver';

import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import {
  IsSupported,
  STARDOG_FEATURE_SUPPORT,
  StardogFeature,
  StardogFeatureSupported,
  createInitialFeaturesState,
} from 'src/store/features/types';
import { capitalize } from 'src/utils/capitalize';

const getIsSupportedKey = <TFeature extends string>(
  feature: TFeature
): IsSupported<TFeature> => `is${capitalize(feature)}Supported` as const;

export const featuresSlice = createSlice({
  name: 'features',
  initialState: createInitialFeaturesState,
  reducers: {},
  extraReducers: ({ addCase }) => {
    addCase(connectionsSlice.actions.disconnected, () =>
      createInitialFeaturesState()
    );
    addCase(
      connectionAsyncActions.requestServerStatus.fulfilled,
      (state, action) => {
        const { connection } = action.meta.arg;
        const stardogVersion: string | undefined =
          action.payload.body?.['dbms.version']?.value;
        state.stardogByConnectionId[connection.id] = (
          Object.keys(STARDOG_FEATURE_SUPPORT) as StardogFeature[]
        ).reduce((acc, key) => {
          acc[getIsSupportedKey(key)] = stardogSupports(key, stardogVersion);
          return acc;
        }, {} as StardogFeatureSupported);
      }
    );
  },
});

const stardogSupports = (
  featureName: keyof typeof STARDOG_FEATURE_SUPPORT,
  version = '0.0.0'
) => {
  const semverVersion = semver.coerce(version);
  if (!semverVersion) {
    return false;
  }

  const support = STARDOG_FEATURE_SUPPORT[featureName];
  const isMinSupported = support.minVersion
    ? semver.gte(semverVersion, support.minVersion)
    : true;
  const isMaxSupported = support.maxVersion
    ? semver.lte(semverVersion, support.maxVersion)
    : true;

  return isMinSupported && isMaxSupported;
};
